import { memo } from 'react'
import Markdown, { defaultUrlTransform } from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'

import { cm } from '~/utils/utils'
import { extractDWLCode } from '~/utils/shared'

import { a } from './a'
import { Code as code, Pre as pre } from './code'
import { h1, h2, h3, h4, h5, h6 } from './header'
import { Img as img } from './img'
import { strong } from './strong'
import { table, td, th, tr } from './table'

import styles from './markdown.module.scss'

// ─────────────────────────────────────────────────────────────────────────────
//  Doc markdown rules + renderer.
//
//  This file owns BOTH the canonicalization pipeline (`prepareDocMarkdown` and
//  its building blocks) AND the React renderer (`DocMarkdown`). They share a
//  domain — rules that make doc markdown render and export correctly — and the
//  rules have no consumer outside the doc layer (the docx/pdf export path
//  imports `prepareDocMarkdown` from here; chat deliberately does not).
//
//  Layered:
//    HELPERS       — table-cell counting / separator detection
//    SAFEGUARDS    — correctness fixups (no-op for clean content)
//    STRUCTURAL    — presentation rewrites (DataWeave fence; lossy, opt-in)
//    PIPELINE      — `prepareDocMarkdown` composes the full doc set
//    RENDERER      — applies only the universal pipe-escape safeguard so the
//                    many call sites that pass raw text are protected without
//                    over-applying the lossy/structural rules.
// ─────────────────────────────────────────────────────────────────────────────

// ─── HELPERS ──────────────────────────────────────────────────────────────────

/** A GFM separator row, e.g. `|---|:--:|---|` — only pipes, dashes, colons, spaces. */
export function isTableSeparatorRow(line: string): boolean {
  return line.includes('-') && /^\s*\|?[\s:|-]+\|?\s*$/.test(line)
}

/** Count the cells a GFM parser sees in a table row: split on UNESCAPED pipes,
 *  ignoring the optional leading/trailing edge pipes (`\|` is already literal). */
export function countTableCells(line: string): number {
  let s = line.trim()
  if (s.startsWith('|')) s = s.slice(1)
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1)
  let cells = 1
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '|' && s[i - 1] !== '\\') cells++
  }
  return cells
}

// ─── SAFEGUARDS (correctness) ─────────────────────────────────────────────────

function escapeProtectedPipesInRow(line: string): string {
  let out = ''
  let inCode = false
  let inQuote = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    const prev = line[i - 1]
    if (ch === '`' && prev !== '\\' && !inQuote) {
      inCode = !inCode
      out += ch
    } else if (ch === '"' && prev !== '\\' && !inCode) {
      inQuote = !inQuote
      out += ch
    } else if (ch === '|' && prev !== '\\' && (inCode || inQuote)) {
      out += '\\|'
    } else {
      out += ch
    }
  }
  return out
}

/**
 * Repair tables whose body/header rows parse to MORE cells than the separator
 * row defines — the tell of an unescaped literal pipe inside a quoted/code cell
 * (e.g. an HL7 field separator written as `"|"`). Escaping those pipes makes
 * the row parse with the intended column count. Idempotent. Applied universally
 * by the renderer below AND inside `prepareDocMarkdown` for the export path.
 */
export function escapeQuotedTablePipes(content: string): string {
  if (!content.includes('|')) return content
  const lines = content.split('\n')
  let modified = false
  for (let i = 0; i + 1 < lines.length; i++) {
    if (!lines[i].includes('|') || !isTableSeparatorRow(lines[i + 1])) continue
    const expected = countTableCells(lines[i + 1])
    const rowIdxs = [i]
    let j = i + 2
    while (j < lines.length && lines[j].includes('|') && lines[j].trim() !== '') {
      rowIdxs.push(j)
      j++
    }
    for (const idx of rowIdxs) {
      if (countTableCells(lines[idx]) > expected) {
        const fixed = escapeProtectedPipesInRow(lines[idx])
        if (fixed !== lines[idx]) {
          lines[idx] = fixed
          modified = true
        }
      }
    }
    i = j - 1
  }
  return modified ? lines.join('\n') : content
}

const PNG_BASE64_PREFIX = 'iVBORw0KGgo'
const JPEG_BASE64_PREFIX = '/9j/'

function base64ImageMarkdown(b64: string): string | null {
  if (b64.startsWith(PNG_BASE64_PREFIX)) return `![image](data:image/png;base64,${b64})`
  if (b64.startsWith(JPEG_BASE64_PREFIX)) return `![image](data:image/jpeg;base64,${b64})`
  return null
}

/**
 * Wrap raw base64 image data the agent emitted without image syntax — both the
 * whole-string case (entire doc is one blob) and the per-line case (a base64
 * blob on its own interior line) — so remark sees an image instead of a wall
 * of characters. No-op otherwise.
 */
export function wrapBareBase64Images(content: string): string {
  const whole = base64ImageMarkdown(content.trim())
  if (whole) return whole
  const lines = content.split('\n')
  let modified = false
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    if (line.length > 100 && /^[A-Za-z0-9+/=]+$/.test(line)) {
      const wrapped = base64ImageMarkdown(line)
      if (wrapped) {
        lines[i] = wrapped
        modified = true
      }
    }
  }
  return modified ? lines.join('\n') : content
}

/**
 * Inline reference-style images (`![alt][id]`, `![alt][]`, `![id]`) using their
 * `[id]: url` definitions. Required when a doc is split into independently-parsed
 * pages — a definition on one page can't resolve a reference on another, so the
 * diagram would silently vanish. No-op when there are no definitions.
 */
export function inlineReferenceImages(text: string): string {
  if (!text) return text
  if (!text.includes(']:')) return text

  const defs = new Map<string, string>()
  const defPattern = /^[ ]{0,3}\[([^\]]+)\]:[ \t]*(\S+)/gm
  let match: RegExpExecArray | null
  while ((match = defPattern.exec(text)) !== null) {
    defs.set(match[1].toLowerCase().trim(), match[2])
  }
  if (defs.size === 0) return text

  return text
    .replace(/!\[([^\]]*)\]\[([^\]]+)\]/g, (full, alt: string, id: string) => {
      const url = defs.get(id.toLowerCase().trim())
      return url ? `![${alt}](${url})` : full
    })
    .replace(/!\[([^\]]+)\]\[[ \t]*\]/g, (full, alt: string) => {
      const url = defs.get(alt.toLowerCase().trim())
      return url ? `![${alt}](${url})` : full
    })
    .replace(/!\[([^\]]+)\](?!\[|\()/g, (full, id: string) => {
      const url = defs.get(id.toLowerCase().trim())
      return url ? `![${id}](${url})` : full
    })
}

// ─── STRUCTURAL (presentation rewrite, opt-in) ────────────────────────────────

/**
 * If the content is a bare DataWeave script (no existing code fence), wrap it
 * in a ```dwl fence so it renders as a code block rather than prose. A
 * presentation choice, opt-in via `prepareDocMarkdown` — NOT applied by the
 * renderer below because the rewrite is lossy (whole-content replacement) and
 * would over-apply to non-doc DocMarkdown usages (blocks, campaign, modals, …).
 */
export function wrapDataWeaveInCodeFence(content: string): string {
  const trimmed = content.trim()
  if (trimmed.includes('```')) return content
  const dwl = extractDWLCode(trimmed)
  return dwl ? `\`\`\`dwl\n${dwl}\n\`\`\`` : content
}

// ─── PIPELINE ─────────────────────────────────────────────────────────────────

/**
 * Full canonicalization for the DOC surface — applied identically on the render
 * path (at doc call sites that opt in) and the export path (docx/pdf/md). The
 * stored markdown and the rendered/exported markdown stay in sync. Idempotent.
 * Chat content never contains these constructs, so chat has no equivalent
 * pipeline; chat's narrow rule lives in `ChatMarkdown.tsx`.
 */
export function prepareDocMarkdown(content: string): string {
  if (!content) return content
  let out = wrapBareBase64Images(content)
  out = wrapDataWeaveInCodeFence(out)
  out = inlineReferenceImages(out)
  return escapeQuotedTablePipes(out)
}

// ─── RENDERER ─────────────────────────────────────────────────────────────────

interface Props {
  text: string
  fontSize?: 'sm' | 'base'
  className?: string
}

const DocMarkdown = ({ text, fontSize = 'base', className }: Props) => {
  // Universal safeguard: always escape literal in-cell pipes before parsing.
  // Idempotent, no-op for content without offending tables, and safe to apply
  // even to non-doc usages of DocMarkdown (blocks, campaign, modals, …).
  const safeText = escapeQuotedTablePipes(text)
  return (
    <Markdown
      className={cm(
        styles.markdown,
        'dark:bg-medium7 text-black dark:text-white',
        fontSize === 'sm' ? 'text-sm' : 'text-base',
        fontSize === 'sm' ? 'pl-5 marker-top' : 'pl-10',
        className
      )}
      components={{
        code: code as any,
        pre: pre as any,
        a,
        img,
        tr,
        td,
        th,
        table,
        strong,
        h1,
        h2,
        h3,
        h4,
        h5,
        h6
      }}
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      urlTransform={(url: string) => {
        // react-markdown's default sanitizer drops anything outside its safe-
        // protocol allowlist. `data:` (inline base64 PNGs from agent-generated
        // docs) and `blob:` (object URLs from download/preview flows) are
        // legitimate sources we control — let them through unmodified.
        if (url.startsWith('data:') || url.startsWith('blob:')) {
          return url
        }
        return defaultUrlTransform(url)
      }}
    >
      {safeText}
    </Markdown>
  )
}

export default memo(DocMarkdown, (prev, next) => prev.text === next.text)
